import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Search, SearchX, X } from 'lucide-react';
import {
  COUNTRIES,
  COUNTRIES_BY_ISO,
  flagUrl,
  searchCountries,
} from './countries';
import styles from './PhoneVerificationModal.module.scss';

function buildRows(query, selectedIso, suggestedIso) {
  const rows = [];
  const selectable = [];

  const pushCountry = (country, sub) => {
    const selectableIndex = selectable.length;
    selectable.push(country.iso);
    rows.push({
      type: 'country',
      country,
      sub: sub || '',
      selected: country.iso === selectedIso,
      selectableIndex,
    });
  };

  const trimmed = (query || '').trim();
  if (trimmed) {
    searchCountries(trimmed).forEach((c) => pushCountry(c));
    return { rows, selectable };
  }

  rows.push({ type: 'header', text: 'Suggested' });
  const suggested = COUNTRIES_BY_ISO[suggestedIso] || COUNTRIES_BY_ISO.GB;
  if (suggested) {
    pushCountry(suggested, 'Based on your location');
  }

  let letter = '';
  COUNTRIES.forEach((c) => {
    const L = c.name.charAt(0).toUpperCase();
    if (L !== letter) {
      letter = L;
      rows.push({ type: 'header', text: L });
    }
    pushCountry(c);
  });

  return { rows, selectable };
}

/**
 * Flag-variant country calling-code picker.
 * Mobile: full-height sheet. Desktop: popover under the phone row.
 */
const CountryCallingCodePicker = ({
  open,
  mode,
  selectedIso,
  suggestedIso,
  onSelect,
  onClose,
  activeIndex,
  onActiveIndexChange,
}) => {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      onActiveIndexChange(0);
      const t = setTimeout(() => {
        if (searchRef.current) searchRef.current.focus();
      }, 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const { rows, selectable } = useMemo(
    () => buildRows(query, selectedIso, suggestedIso),
    [query, selectedIso, suggestedIso]
  );

  useEffect(() => {
    if (!open || !listRef.current) return;
    if (activeIndex < 0 || activeIndex >= selectable.length) return;
    const el = listRef.current.querySelector(
      `[data-selectable-index="${activeIndex}"]`
    );
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open, selectable]);

  if (!open) return null;

  const handleKeyDown = (e) => {
    const n = selectable.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onActiveIndexChange(Math.min(n - 1, activeIndex + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onActiveIndexChange(Math.max(0, activeIndex - 1));
    } else if (e.key === 'Enter' && n) {
      e.preventDefault();
      onSelect(selectable[activeIndex] || selectable[0]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const content = (
    <>
      {mode === 'sheet' && (
        <div className={styles.sheetPickerHeader}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label="Back"
            onClick={onClose}
          >
            <ArrowLeft size={22} strokeWidth={2.2} />
          </button>
          <h2 className={styles.title}>Country or region</h2>
        </div>
      )}

      <div className={styles.searchWrap}>
        <Search
          size={mode === 'sheet' ? 18 : 16}
          className={styles.searchIcon}
        />
        <input
          ref={searchRef}
          className={styles.searchInput}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-label="Search country or code"
          placeholder="Search country or code"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onActiveIndexChange(0);
          }}
          onKeyDown={handleKeyDown}
        />
        {query ? (
          <button
            type="button"
            className={styles.clearSearch}
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              onActiveIndexChange(0);
            }}
          >
            <X size={mode === 'sheet' ? 16 : 14} />
          </button>
        ) : null}
      </div>

      <div
        ref={listRef}
        role="listbox"
        aria-label="Countries"
        className={mode === 'sheet' ? styles.sheetList : styles.popoverList}
      >
        {rows.map((row, idx) => {
          if (row.type === 'header') {
            return (
              <div key={`h-${row.text}-${idx}`} className={styles.listHeader}>
                {row.text}
              </div>
            );
          }
          const { country, sub, selected, selectableIndex } = row;
          const active =
            selectableIndex === activeIndex && !selected
              ? styles.countryOptionActive
              : '';
          return (
            <button
              key={`country-${selectableIndex}`}
              type="button"
              role="option"
              data-iso={country.iso}
              data-selectable-index={selectableIndex}
              aria-selected={selected}
              className={`${styles.countryOption} ${
                selected ? styles.countryOptionSelected : ''
              } ${active}`}
              onClick={() => onSelect(country.iso)}
            >
              <img
                src={flagUrl(country.iso)}
                alt=""
                className={styles.flagSm}
              />
              <span className={styles.countryMeta}>
                <span className={styles.countryName}>{country.name}</span>
                {sub ? <span className={styles.countrySub}>{sub}</span> : null}
              </span>
              <span className={styles.dialCode}>+{country.dial}</span>
              <span className={styles.checkSlot}>
                {selected ? (
                  <Check size={mode === 'sheet' ? 18 : 16} strokeWidth={2.4} />
                ) : null}
              </span>
            </button>
          );
        })}
        {selectable.length === 0 ? (
          <div className={styles.noResults}>
            <SearchX size={22} />
            <span className={styles.noResultsTitle}>
              No countries match “{query}”
            </span>
            <span className={styles.noResultsHint}>
              Try the country name or its dialling code, e.g. 44.
            </span>
          </div>
        ) : null}
      </div>
    </>
  );

  if (mode === 'sheet') {
    return <div className={styles.sheetPicker}>{content}</div>;
  }

  return (
    <div className={styles.popover} role="presentation">
      {content}
    </div>
  );
};

CountryCallingCodePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['sheet', 'popover']).isRequired,
  selectedIso: PropTypes.string.isRequired,
  suggestedIso: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onActiveIndexChange: PropTypes.func.isRequired,
};

export default CountryCallingCodePicker;
